import { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { CalendarDays, CheckCircle2, ChevronDown, ChevronUp, Target, ListTodo } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const DAY_STYLE = {
  complete: { chip: 'bg-[#1B2A4A] text-white border-[#1B2A4A]', dot: 'bg-green-400' },
  current: { chip: 'bg-[#D9480F] text-white border-[#D9480F] ring-4 ring-[#D9480F]/20', dot: 'bg-white' },
  upcoming: { chip: 'bg-white text-stone-600 border-stone-200', dot: 'bg-stone-300' },
};

export default function TrainingSchedule({ schedule, navigate }) {
  const currentIdx = Math.max(0, schedule.findIndex(d => d.status === 'current'));
  const [openIdx, setOpenIdx] = useState(currentIdx);
  const [expanded, setExpanded] = useState(true);
  if (!schedule?.length) return null;
  const day = schedule[openIdx];
  const doneCount = schedule.filter(d => d.status === 'complete').length;

  return (
    <Card className="rounded-2xl border border-stone-200 bg-white shadow-sm mb-5" data-testid="training-schedule">
      <CardContent className="p-4">
        <button onClick={() => setExpanded(!expanded)} className="w-full flex items-center justify-between" data-testid="schedule-toggle">
          <div className="flex items-center gap-2">
            <CalendarDays className="w-4 h-4 text-[#D9480F]" />
            <h4 className="text-sm font-semibold text-stone-900" style={{ fontFamily: "'Outfit', sans-serif" }}>Your Training Schedule</h4>
            <span className="text-xs text-stone-400">{doneCount}/{schedule.length} days complete</span>
          </div>
          {expanded ? <ChevronUp className="w-4 h-4 text-stone-400" /> : <ChevronDown className="w-4 h-4 text-stone-400" />}
        </button>
        <AnimatePresence initial={false}>
          {expanded && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
              <div className="flex gap-2 overflow-x-auto pb-2 mt-3 -mx-1 px-1" data-testid="schedule-days">
                {schedule.map((d, i) => {
                  const st = DAY_STYLE[d.status] || DAY_STYLE.upcoming;
                  return (
                    <button key={d.id || i} onClick={() => setOpenIdx(i)}
                      className={`shrink-0 rounded-xl border px-3 py-2 text-left transition-all min-w-[110px] ${st.chip} ${openIdx === i ? 'shadow-md' : 'opacity-90 hover:opacity-100'}`}
                      data-testid={`schedule-day-chip-${i}`}>
                      <div className="flex items-center gap-1.5">
                        {d.status === 'complete' ? <CheckCircle2 className="w-3 h-3 text-green-300" /> : <span className={`w-2 h-2 rounded-full ${st.dot}`} />}
                        <span className="text-[11px] font-bold uppercase tracking-wider">{d.label}</span>
                      </div>
                      <p className={`text-[11px] mt-0.5 line-clamp-1 ${d.status === 'upcoming' ? 'text-stone-500' : 'text-white/80'}`}>{d.title || `${d.modules.length} module${d.modules.length !== 1 ? 's' : ''}`}</p>
                    </button>
                  );
                })}
              </div>
              {day && (
                <motion.div key={openIdx} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="mt-2 rounded-xl bg-stone-50 border border-stone-100 p-3" data-testid="schedule-day-detail">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-stone-900">{day.label}{day.title ? ` — ${day.title}` : ''}</p>
                    <Badge className={`text-[10px] ${day.status === 'complete' ? 'bg-green-100 text-green-700' : day.status === 'current' ? 'bg-orange-100 text-[#D9480F]' : 'bg-stone-200 text-stone-600'}`}>{day.status === 'complete' ? 'Complete' : day.status === 'current' ? 'You are here' : 'Upcoming'}</Badge>
                  </div>
                  {day.focus && <p className="text-xs text-stone-600 mt-1.5 flex items-start gap-1.5"><Target className="w-3.5 h-3.5 text-[#D9480F] shrink-0 mt-0.5" />{day.focus}</p>}
                  {day.modules.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {day.modules.map(m => (
                        <button key={m.id} onClick={() => m.status !== 'locked' && navigate(`/training/${m.id}`)} disabled={m.status === 'locked'}
                          className={`w-full text-left text-xs px-2.5 py-1.5 rounded-lg border flex items-center justify-between gap-2 ${m.status === 'locked' ? 'border-stone-200 text-stone-400 bg-white' : 'border-stone-200 text-stone-800 bg-white hover:border-[#1B2A4A]/40'}`}
                          data-testid={`schedule-module-${m.id}`}>
                          <span className="truncate">{m.title}</span>
                          <span className={`shrink-0 text-[10px] ${m.status === 'approved' ? 'text-green-600' : m.status === 'locked' ? 'text-stone-400' : 'text-[#D9480F]'}`}>{m.status === 'approved' ? 'Signed off' : m.status === 'locked' ? 'Locked' : m.status === 'pending_signoff' ? 'Awaiting sign-off' : 'Open'}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  {day.activities?.length > 0 && (
                    <div className="mt-2">
                      <p className="text-[10px] uppercase tracking-wider text-stone-400 font-semibold flex items-center gap-1"><ListTodo className="w-3 h-3" /> On the floor</p>
                      <ul className="mt-1 space-y-0.5">{day.activities.map((a, i) => <li key={i} className="text-xs text-stone-600 flex gap-1.5"><span className="text-[#D9480F]">•</span>{a}</li>)}</ul>
                    </div>
                  )}
                </motion.div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </CardContent>
    </Card>
  );
}
